import { getPaymentusDaysMessage } from 'app-sections/admin/settings/components/general-settings/components/paymentus-info-settings/helpers';
import { getLinkToDetails } from 'app-sections/user/applications/steps/config';
import { useGetSettingsQuery } from 'core/api/settings/queries';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { ApplicationPaymentState, IPaymentusSettings } from 'types';
import LinesSkeleton from 'ui/components/skeletons/lines-skeleton/LinesSkeleton';
import StatusCard, { StatusCardState } from 'ui/components/status-card/StatusCard';
import { scrollToSectionId } from 'ui/helpers/scrollToSectionId';

import { Box, Button } from '@mui/material';

const getCardState = (type: ApplicationPaymentState): StatusCardState => {
  if (type === 'failed') {
    return 'failed';
  }

  if (type === 'paid') {
    return 'success';
  }

  return 'pending';
};

const getTitle = (type: ApplicationPaymentState): string => {
  if (type === 'failed') {
    return 'Failed payment';
  }

  if (type === 'paid') {
    return 'Successful payment';
  }

  return 'Payment processing';
};

const getContent = (type: ApplicationPaymentState, paymentusSettings: IPaymentusSettings): string | JSX.Element => {
  if (type === 'failed') {
    return (
      <>
        <p>Your payment attempt failed for some reason. Please try again after checking the following:</p>
        <ul>
          <li>Ensure your payment method is valid and has sufficient funds.</li>
          <li>Verify that all payment details (card number, expiration date, and security code) are correct.</li>
        </ul>
        <p>
          If the issue persists, <a href="mailto:dob@dc.gov">contact us</a> for support.
        </p>
      </>
    );
  }

  if (type === 'paid') {
    return 'Payment has been successfully processed. The Authorization Form is accepted and the Wall Check application number is created.';
  }

  return (
    <>
      Processing payment may take up a few minutes in case of using a card, and up to{' '}
      {getPaymentusDaysMessage(paymentusSettings.days)} days when bank account was used.
    </>
  );
};

const getButtonForStatus = (
  status: ApplicationPaymentState,
  applicationId: string,
  navigate: NavigateFunction
): JSX.Element | null => {
  switch (status) {
    case 'failed':
      return getButton('Try again', () => scrollToSectionId('payments-section'));
    case 'paid':
      return getButton('Download form', () => {
        navigate(getLinkToDetails(applicationId, 'summary?payment_status=success#download-form-section'));
        scrollToSectionId('download-form-section');
      });
  }
  return null;
};

const getButton = (label: string, onClickHandle: () => void): JSX.Element | null => {
  return (
    <Button type="button" variant="contained" color="primary" disableElevation onClick={onClickHandle}>
      {label}
    </Button>
  );
};

export interface PaymentStatusInfoProps {
  status: ApplicationPaymentState;
  applicationId: string;
}

export default function PaymentStatusInfo(props: PaymentStatusInfoProps) {
  const { status, applicationId } = props;
  const navigate = useNavigate();

  const { isLoading, isError, data: settings, error } = useGetSettingsQuery();

  if (isLoading || isError || !settings) {
    return (
      <Box mb={3}>
        <LinesSkeleton elements={4} spacing={2} isWithButtons={false} />
      </Box>
    );
  }

  return (
    <StatusCard
      title={getTitle(status)}
      content={getContent(status, settings.paymentusSettings)}
      isLoading={status === 'pending'}
      status={getCardState(status)}
      button={getButtonForStatus(status, applicationId, navigate)}
    />
  );
}
